// Timeline pattern adapted from Hyperiux Vault: https://vault.hyperiux.com
// Structure kept faithful to the original (vw-based so everything scales
// together). Font sizes are clamped so it never grows oversized on wide
// screens. SplitText (paid plugin) replaced by masked y-reveals.
import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "../hooks/useGSAP";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const DESKTOP_POSITIONS = [
  [4, 22],
  [18, 36],
  [32, 50],
  [46, 64],
  [60, 78],
  [70, 88],
  [78, 94],
];

const MOBILE_POSITIONS = [
  [16, 26],
  [28, 38],
  [40, 50],
  [52, 62],
  [64, 74],
  [72, 82],
  [78, 92],
];

function getSlideDistance(slider) {
  if (!slider) return 0;
  const isMobile = window.innerWidth < 600;
  const buffer = isMobile ? 32 : 20;
  return Math.max(0, slider.offsetWidth - window.innerWidth + buffer);
}

function sortItems(items) {
  return [...items].sort((a, b) => a.order - b.order);
}

const TITLE = "text-[clamp(15px,1.5vw,24px)] max-[600px]:text-[5.6vw] leading-tight font-semibold text-foreground";
const DESC = "text-[clamp(12px,0.95vw,15px)] max-[600px]:text-[4vw] leading-snug text-muted-foreground";
const META = "text-[clamp(10px,0.75vw,12px)] max-[600px]:text-[3.2vw] font-mono text-muted-foreground";

export default function ProjectsTimeline({
  lang,
  title,
  subtitle,
  topItems = [],
  bottomItems = [],
  imageUrl,
  imageAlt,
  activeColor = "hsl(215 45% 52%)",
  scrollDuration = 1.2,
}) {
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();
  const normalizedDuration = Math.max(0.2, scrollDuration);
  const allItems = sortItems([...topItems, ...bottomItems]);
  const activeStyle = { backgroundColor: activeColor };

  // Slide + horizontal line
  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section) return;

      const isMobile = window.innerWidth < 600;
      const lineWidth = isMobile ? "calc(100% - 4vw)" : "98%";
      const lineStart = isMobile ? "top 30%" : "top 25%";
      const slideEnd = isMobile ? "88% 50%" : "92% bottom";

      const line = section.querySelector(".journey-line");
      const lineTrack = line?.parentElement;

      // Mobile: the tip follows a fixed point on screen so it is always visible.
      const syncMobileLine = () => {
        if (!line || !lineTrack) return;
        const trackRect = lineTrack.getBoundingClientRect();
        const max = trackRect.width - window.innerWidth * 0.04;
        const tipTarget = window.innerWidth * 0.75;
        const width = Math.min(max, Math.max(0, tipTarget - trackRect.left));
        line.style.width = `${width}px`;
      };

      gsap
        .timeline({
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: slideEnd,
            scrub: 0.6,
            invalidateOnRefresh: true,
          },
          defaults: { ease: "none", force3D: true },
          onUpdate: isMobile && !reducedMotion ? syncMobileLine : undefined,
        })
        .fromTo(
          sliderRef.current,
          { x: 0 },
          {
            x: () => -getSlideDistance(sliderRef.current),
          },
        );

      if (reducedMotion) {
        gsap.set(".journey-line", { width: lineWidth });
        return;
      }

      if (isMobile) {
        syncMobileLine();
        return;
      }

      gsap.to(".journey-line", {
        width: lineWidth,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: lineStart,
          end: slideEnd,
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
      });
    },
    { dependencies: [reducedMotion, lang], scope: sectionRef },
  );

  // Per-item reveals
  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section || allItems.length === 0) return;

      if (reducedMotion) {
        allItems.forEach((item) => {
          gsap.set(`.jl-${item.id}`, { scaleY: 1 });
          gsap.set(`.jd-${item.id}`, { scale: 1 });
          gsap.set(`.title-${item.id}, .description-${item.id}, .meta-${item.id}`, {
            yPercent: 0,
          });
        });
        return;
      }

      allItems.forEach((item) => {
        const isTop = topItems.some((t) => t.id === item.id);
        gsap.set(`.jl-${item.id}`, {
          scaleY: 0,
          transformOrigin: isTop ? "bottom bottom" : "top top",
        });
        gsap.set(`.jd-${item.id}`, { scale: 0 });
        gsap.set(`.title-${item.id}, .description-${item.id}, .meta-${item.id}`, {
          yPercent: 110,
        });
      });

      const positions =
        window.innerWidth < 600 ? MOBILE_POSITIONS : DESKTOP_POSITIONS;

      allItems.forEach((item, index) => {
        const [startPos, endPos] =
          positions[index] ?? positions[positions.length - 1];

        gsap
          .timeline({
            scrollTrigger: {
              trigger: section,
              start: `${startPos}% 30%`,
              end: `${endPos}% 50%`,
              scrub: 0.6,
              invalidateOnRefresh: true,
            },
            defaults: { force3D: true },
          })
          .to(`.jl-${item.id}`, {
            scaleY: 1,
            duration: normalizedDuration * 0.4,
          })
          .to(
            `.jd-${item.id}`,
            { scale: 1, duration: normalizedDuration * 0.4 },
            "<",
          )
          .to(
            `.title-${item.id}`,
            {
              yPercent: 0,
              duration: normalizedDuration,
              ease: "power2.out",
            },
            `-=${0.8 * normalizedDuration}`,
          )
          .to(
            `.description-${item.id}`,
            {
              yPercent: 0,
              duration: normalizedDuration,
              ease: "power2.out",
            },
            "<0.05",
          )
          .to(
            `.meta-${item.id}`,
            {
              yPercent: 0,
              duration: normalizedDuration * 0.8,
              ease: "power2.out",
            },
            "<0.05",
          );
      });
    },
    {
      dependencies: [normalizedDuration, reducedMotion, lang, allItems.length],
      scope: sectionRef,
    },
  );

  const renderMeta = (item) => item.tags.join(" · ");

  return (
    <section
      ref={sectionRef}
      id="projects"
      data-testid="projects-section"
      className="relative w-full h-[170vw] max-[600px]:h-[400vh] bg-background border-b border-border"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden pt-[9%] max-[600px]:top-0 max-[600px]:pt-24">
        <div
          ref={sliderRef}
          className="mr-[2vw] flex h-[26vw] w-[205vw] items-center gap-[4vw] px-[5vw] will-change-transform max-[600px]:h-[calc(100vh-8rem)] max-[600px]:min-w-max max-[600px]:w-max max-[600px]:pr-8 max-[600px]:px-[7vw]"
        >
          {/* Imagem */}
          <div className="h-full w-[24vw] shrink-0 overflow-hidden rounded-[0.8vw] border border-border max-[600px]:h-[65vw] max-[600px]:w-[85vw] max-[600px]:rounded-[4vw]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={imageAlt || ""}
                draggable={false}
                onLoad={() => ScrollTrigger.refresh()}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-secondary" />
            )}
          </div>

          <div className="relative h-full w-full max-[600px]:w-max">
            {/* Linha horizontal central */}
            <div className="absolute left-0 top-[49%] flex h-fit w-full items-center">
              <div
                className="h-[0.6vw] w-[0.6vw] shrink-0 rounded-full max-[600px]:h-[2vw] max-[600px]:w-[2vw]"
                style={activeStyle}
              />
              <div className="journey-line h-px w-[0%] rounded-full" style={activeStyle} />
              <div
                className="h-[0.6vw] w-[0.6vw] shrink-0 rounded-full max-[600px]:h-[2vw] max-[600px]:w-[2vw]"
                style={activeStyle}
              />
            </div>

            {/* Metade superior */}
            <div className="flex h-1/2 w-full items-center justify-start gap-[0.5vw]">
              <div className="h-full w-[20%] shrink-0 pt-[1.5vw] max-[600px]:h-fit max-[600px]:w-[60vw] max-[600px]:pt-[5vw]">
                <h2 className="w-[80%] text-[clamp(20px,2.3vw,38px)] max-[600px]:text-[8vw] leading-[1] font-semibold text-foreground">
                  {title}
                </h2>
                <p className="mt-[1vw] w-[85%] text-[clamp(11px,0.85vw,14px)] max-[600px]:text-[3.6vw] leading-snug text-muted-foreground">
                  {subtitle}
                </p>
              </div>

              <div className="flex h-full w-full gap-x-[12vw] max-[600px]:gap-x-[10vw]">
                {topItems.map((item) => (
                  <div
                    key={`top-${item.id}`}
                    className="relative h-full w-[26vw] shrink-0 px-[2.5vw] max-[600px]:flex max-[600px]:w-[68vw] max-[600px]:flex-col max-[600px]:px-[4vw]"
                  >
                    <div className="absolute bottom-0 left-0 top-0 h-full w-full">
                      <div
                        className={`jd-${item.id} relative aspect-square size-[0.8vw] -translate-x-1/2 rounded-full max-[600px]:size-[2.5vw]`}
                        style={activeStyle}
                      />
                      <div
                        className={`jl-${item.id} h-[94%] w-px origin-bottom rounded-full`}
                        style={activeStyle}
                      />
                    </div>

                    <div className="mt-[-0.6vw] space-y-[0.6vw] max-[600px]:mt-[-2vw]">
                      <div className="overflow-hidden">
                        <h4 className={`title-${item.id} ${TITLE}`}>{item.title}</h4>
                      </div>
                      <div className="overflow-hidden">
                        <p className={`description-${item.id} w-[92%] ${DESC}`}>
                          {item.desc}
                        </p>
                      </div>
                      <div className="overflow-hidden">
                        <p className={`meta-${item.id} ${META}`}>{renderMeta(item)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Metade inferior */}
            <div className="flex h-1/2 w-full items-center justify-start">
              <div className="h-full w-[34%] shrink-0 max-[600px]:w-[40vw]" aria-hidden="true" />

              <div className="ml-[6vw] flex h-full w-full gap-x-[16vw] max-[600px]:ml-[7vw] max-[600px]:gap-x-[10vw]">
                {bottomItems.map((item) => (
                  <div
                    key={`bottom-${item.id}`}
                    className="relative h-full w-[24vw] shrink-0 px-[2.5vw] max-[600px]:w-[68vw] max-[600px]:px-[4vw]"
                  >
                    <div className="absolute bottom-[-1%] left-0 h-full w-full">
                      <div
                        className={`jl-${item.id} h-[94%] w-px origin-top rounded-full max-[600px]:h-full`}
                        style={activeStyle}
                      />
                      <div
                        className={`jd-${item.id} relative aspect-square size-[0.8vw] -translate-x-1/2 rounded-full max-[600px]:size-[2.5vw]`}
                        style={activeStyle}
                      />
                    </div>

                    <div className="flex h-full w-full flex-col justify-end space-y-[0.6vw]">
                      <div className="overflow-hidden">
                        <h4 className={`title-${item.id} ${TITLE}`}>{item.title}</h4>
                      </div>
                      <div className="overflow-hidden">
                        <p className={`description-${item.id} w-[92%] ${DESC}`}>
                          {item.desc}
                        </p>
                      </div>
                      <div className="overflow-hidden">
                        <p className={`meta-${item.id} ${META}`}>{renderMeta(item)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
